import { Zap, ShieldCheck, BadgeCheck, Sparkles, History, PlayCircle, Activity, Store, Briefcase } from "lucide-react";
import { Section } from "./Section";

const PILLARS = [
  [Zap, "Research at machine speed", "Stop stitching together screeners, spreadsheets and notes. Describe the idea in plain English and the AI turns it into complete, editable rules you can inspect line by line."],
  [ShieldCheck, "Proof before capital", "Every strategy is tested on 10+ years of NSE & BSE data — including brokerage, slippage and taxes — then paper traded on live signals so you see behaviour before you risk a rupee."],
  [BadgeCheck, "Verified voices only", "The strategy marketplace is open to SEBI-registered Research Analysts. Publisher identity, registrations and live track records sit next to every strategy."],
];

const MODULES = [
  [Sparkles, "AI builder", "AI Strategy Builder", "Describe a setup the way you would to a friend — \"buy NIFTY on a 15-minute opening-range breakout above the first hour high, exit at 1:30 or on a 0.4% stop\". The AI returns entry, exit and stop-loss rules you can edit, version and re-test."],
  [History, "Backtesting", "Backtesting on historical data", "Test rules against 10+ years of NSE & BSE price data with costs, slippage and position sizing modelled. See equity curve, CAGR, max drawdown, Sharpe and trade-by-trade logs."],
  [PlayCircle, "Paper trading", "Paper trading simulator", "Run any strategy on live market signals with virtual capital. Track fills, P&L and discipline in real time — and get flagged when you deviate from your own rules."],
  [Activity, "Live tracking", "Live performance, on the record", "Published strategies show actual fills, live P&L, drawdown and rolling win rate — the same numbers for you, your subscribers and the analyst who wrote it. No edited screenshots."],
  [Store, "Marketplace", "Strategy marketplace for SEBI RAs", "Subscribe to strategies published by SEBI-registered Research Analysts — intraday F&O, positional equity and options income — with their registration details and live record in view."],
  [Briefcase, "Advisory", "Portfolio & advisory services", "Beyond signals: research notes, model portfolios for long-term investors and dedicated advisory mandates for HNIs, mapped to the way you actually want to invest."],
];

const Icon = ({ I }) => (
  <span className="w-9 h-9 rounded-lg border border-[rgba(34,211,238,0.3)] bg-[rgba(34,211,238,0.06)] flex items-center justify-center text-[var(--tn-cyan)]">
    <I size={17} />
  </span>
);

export const Why = () => (
  <Section id="why" testId="why-section" eyebrow="Why Trading Nifty" title="A research desk that works like a trading terminal"
    intro="Most Indian retail traders do not lose money for lack of ideas — they lose it for lack of testing and discipline. Trading Nifty is being built to fix both, in one place.">
    <div className="grid md:grid-cols-3 gap-5">
      {PILLARS.map(([I, t, d], i) => (
        <article key={t} className="tn-card tn-reveal p-6" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`why-pillar-${i}`}>
          <Icon I={I} />
          <h3 className="font-display text-lg font-medium mt-5">{t}</h3>
          <p className="text-sm text-[var(--tn-muted)] mt-3 leading-relaxed">{d}</p>
        </article>
      ))}
    </div>
  </Section>
);

export const Platform = () => (
  <Section id="platform" testId="platform-section" eyebrow="The platform" title="Everything from idea to live deployment"
    intro="Six connected modules — create, test, simulate, follow and subscribe — so a strategy never leaves the platform between research and real money.">
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      {MODULES.map(([I, tag, t, d], i) => (
        <article key={t} className="tn-card tn-reveal p-6" style={{ transitionDelay: `${(i % 3) * 80}ms` }} data-testid={`platform-module-${i}`}>
          <div className="flex items-center justify-between">
            <Icon I={I} />
            <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--tn-muted)]">{tag}</span>
          </div>
          <h3 className="font-display text-lg font-medium mt-5">{t}</h3>
          <p className="text-sm text-[var(--tn-muted)] mt-3 leading-relaxed">{d}</p>
        </article>
      ))}
    </div>
  </Section>
);
